// src/components/shared/modals/UnifiedCourseModal/index.tsx
// Unified modal for adding courses across all contexts (semesters, bookmarks, other credits).
// Two tabs: Search Courses and Create Placeholder.
// Context-aware: Other Credits shows additional credit source fields.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Loader2 } from 'lucide-react';
import { ModalShell } from '../../../ui';
import { typedApi } from '../../../../services/typedApiClient';
import SearchPanel, {
  type SearchableCourse,
  type CourseModalContext,
  type CourseModalMode,
} from './SearchPanel';
import PlaceholderForm, {
  type PlaceholderFormDataShape,
  type PlaceholderFormErrors,
} from './PlaceholderForm';
import type { PlaceholderTemplate } from './constants';

interface PLCInfoLite {
  total_plc?: number;
  remaining: number;
  plc_cap: number;
}

interface AcademicPlanLite {
  semesters?: Record<string, { courses?: Array<{ code?: string }> } | null | undefined>;
  bookmarkedCourses?: Array<{ code?: string } | string>;
  [key: string]: unknown;
}

interface EditingPlaceholder {
  title?: string;
  credits?: number;
  placeholderType?: string;
  notes?: string;
  sourceType?: string;
  institution?: string;
  grade?: string;
  requirements?: {
    department?: string;
    levelMin?: number | string;
    levelMax?: number | string;
    genEdCategories?: string[];
    keywords?: string[];
  };
  [key: string]: unknown;
}

export type UnifiedCourseModalSubmitData = Record<string, unknown>;

export interface UnifiedCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    courseData: UnifiedCourseModalSubmitData,
    targetSemester: string | null
  ) => Promise<void> | void;
  context?: CourseModalContext;
  targetSemester?: string | null;
  editingPlaceholder?: EditingPlaceholder | null;
  academicPlan?: AcademicPlanLite | null;
  plcInfo?: PLCInfoLite | null;
}

const DEFAULT_PLACEHOLDER_DATA: PlaceholderFormDataShape = {
  title: '',
  credits: 3,
  placeholderType: 'elective',
  notes: '',
  requirements: {
    department: '',
    levelMin: '',
    levelMax: '',
    genEdCategories: [],
    keywords: [],
  },
  sourceType: 'community_college',
  institution: '',
  grade: '',
};

function placeholderDataFromExisting(
  placeholder: EditingPlaceholder
): PlaceholderFormDataShape {
  return {
    title: placeholder.title || '',
    credits: placeholder.credits || 3,
    placeholderType: placeholder.placeholderType || 'elective',
    notes: placeholder.notes || '',
    requirements: {
      department: placeholder.requirements?.department || '',
      levelMin: placeholder.requirements?.levelMin || '',
      levelMax: placeholder.requirements?.levelMax || '',
      genEdCategories: placeholder.requirements?.genEdCategories || [],
      keywords: placeholder.requirements?.keywords || [],
    },
    sourceType: placeholder.sourceType || 'community_college',
    institution: placeholder.institution || '',
    grade: placeholder.grade || '',
  };
}

const UnifiedCourseModal = ({
  isOpen,
  onClose,
  onSubmit,
  context = 'semester',
  targetSemester = null,
  editingPlaceholder = null,
  academicPlan = null,
  plcInfo = null,
}: UnifiedCourseModalProps) => {
  const [mode, setMode] = useState<CourseModalMode>('search');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchableCourse[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<SearchableCourse | null>(null);

  const [placeholderData, setPlaceholderData] = useState<PlaceholderFormDataShape>({
    ...DEFAULT_PLACEHOLDER_DATA,
  });

  const [showRequirements, setShowRequirements] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [errors, setErrors] = useState<PlaceholderFormErrors & { course?: string; plc?: string; submit?: string }>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');


  const sortedResults: SearchableCourse[] = useMemo(() => {
    if (!searchResults.length) return [];

    const sorted = [...searchResults];
    switch (sortBy) {
      case 'gpa_desc':
        return sorted.sort((a, b) => {
          const aGpa = a.grade_data?.average_gpa ?? -1;
          const bGpa = b.grade_data?.average_gpa ?? -1;
          return bGpa - aGpa;
        });
      case 'gpa_asc':
        return sorted.sort((a, b) => {
          const aGpa = a.grade_data?.average_gpa ?? 999;
          const bGpa = b.grade_data?.average_gpa ?? 999;
          return aGpa - bGpa;
        });
      case 'code':
        return sorted.sort((a, b) => a.code.localeCompare(b.code));
      default:
        return sorted;
    }
  }, [searchResults, sortBy]);

  const getModalTitle = (): string => {
    if (editingPlaceholder) return 'Edit Placeholder';
    switch (context) {
      case 'bookmark':
        return 'Add Bookmarked Course';
      case 'other_credits':
        return 'Add Other Credit';
      default:
        return 'Add Course';
    }
  };

  const getAccentColor = (): string => {
    switch (context) {
      case 'bookmark':
        return 'stone';
      case 'other_credits':
        return 'indigo';
      default:
        return 'red';
    }
  };

  const accentColor = getAccentColor();

  useEffect(() => {
    if (!isOpen) return;

    if (editingPlaceholder) {
      setMode('placeholder');
      setPlaceholderData(placeholderDataFromExisting(editingPlaceholder));
      const reqs = editingPlaceholder.requirements;
      const hasReqs = !!(
        reqs?.department ||
        reqs?.levelMin ||
        (reqs?.genEdCategories && reqs.genEdCategories.length > 0)
      );
      setShowRequirements(hasReqs);
    } else {
      setMode('search');
      setSearchQuery('');
      setSearchResults([]);
      setSelectedCourse(null);
      setPlaceholderData({ ...DEFAULT_PLACEHOLDER_DATA });
      setShowRequirements(false);
    }
    setKeywordInput('');
    setErrors({});
  }, [isOpen, editingPlaceholder]);

  const isCourseInPlan = useCallback(
    (courseCode: string): boolean => {
      if (!academicPlan?.semesters) return false;
      for (const semester of Object.values(academicPlan.semesters)) {
        if (semester?.courses?.some((c) => c.code === courseCode)) {
          return true;
        }
      }
      return false;
    },
    [academicPlan]
  );

  const isCourseBookmarked = useCallback(
    (courseCode: string): boolean => {
      if (!academicPlan?.bookmarkedCourses) return false;
      return academicPlan.bookmarkedCourses.some((b) =>
        typeof b === 'string' ? b === courseCode : b.code === courseCode
      );
    },
    [academicPlan]
  );

  const performSearch = useCallback(async (query: string): Promise<void> => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await typedApi.courses.search({
        search: query,
        show_availability: true,
        limit: 20,
      });
      const courses =
        (response as { data?: { courses?: SearchableCourse[] }; courses?: SearchableCourse[] })
          .data?.courses ||
        (response as { courses?: SearchableCourse[] }).courses ||
        [];
      setSearchResults(courses);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (mode === 'search') {
        performSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, mode, performSearch]);

  const handleTemplateSelect = (template: PlaceholderTemplate): void => {
    setPlaceholderData((prev) => ({
      ...prev,
      ...template.data,
      requirements: {
        ...prev.requirements,
        ...template.data.requirements,
      },
    }));
    if (
      template.data.requirements &&
      (template.data.requirements.department ||
        template.data.requirements.levelMin ||
        (template.data.requirements.genEdCategories &&
          template.data.requirements.genEdCategories.length > 0))
    ) {
      setShowRequirements(true);
    }
  };

  const handleInputChange = (field: string, value: unknown): void => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setPlaceholderData((prev) => {
        const parentValue = (prev as Record<string, unknown>)[parent];
        const parentObj =
          typeof parentValue === 'object' && parentValue !== null ? parentValue : {};
        return {
          ...prev,
          [parent]: {
            ...(parentObj as Record<string, unknown>),
            [child]: value,
          },
        } as PlaceholderFormDataShape;
      });
    } else {
      setPlaceholderData(
        (prev) =>
          ({
            ...prev,
            [field]: value,
          }) as PlaceholderFormDataShape
      );
    }
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleGenEdToggle = (category: string): void => {
    const current = placeholderData.requirements.genEdCategories;
    const updated = current.includes(category)
      ? current.filter((c) => c !== category)
      : [...current, category];
    handleInputChange('requirements.genEdCategories', updated);
  };

  const handleAddKeyword = (): void => {
    const trimmed = keywordInput.trim();
    if (trimmed && !placeholderData.requirements.keywords.includes(trimmed)) {
      handleInputChange('requirements.keywords', [
        ...placeholderData.requirements.keywords,
        trimmed,
      ]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string): void => {
    handleInputChange(
      'requirements.keywords',
      placeholderData.requirements.keywords.filter((k) => k !== keyword)
    );
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (mode === 'search' && !selectedCourse) {
      newErrors.course = 'Please select a course';
    }
    if (mode !== 'search') {
      if (!placeholderData.title.trim()) newErrors.title = 'Title is required';
      if (placeholderData.credits < 0 || placeholderData.credits > 12) {
        newErrors.credits = 'Credits must be between 0 and 12';
      }
      const { levelMin, levelMax } = placeholderData.requirements;
      if (levelMin && levelMax && parseInt(String(levelMin)) > parseInt(String(levelMax))) {
        newErrors.level = 'Minimum level cannot be greater than maximum';
      }
    }

    if (context === 'other_credits' && plcInfo) {
      const creditsToAdd =
        mode === 'search' ? selectedCourse?.credits || 3 : placeholderData.credits;
      // A null `remaining` means the institution has no PLC cap (UC Merced,
      // W41 M5) — nothing to exceed. `null < n` is `true` in JS, so guard it.
      if (plcInfo.remaining != null && plcInfo.remaining < creditsToAdd) {
        newErrors.plc = `Adding ${creditsToAdd} credits would exceed the ${plcInfo.plc_cap}-credit PLC limit.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let courseData: UnifiedCourseModalSubmitData;

      if (mode === 'search' && selectedCourse) {
        courseData = {
          type: 'umd_course',
          code: selectedCourse.code,
          course_id: selectedCourse.id,
          title: selectedCourse.title,
          credits: selectedCourse.credits,
          isPlaceholder: false,
        };

        if (context === 'other_credits') {
          courseData.grade = placeholderData.grade || null;
          courseData.source_type = 'other_credits';
        }
      } else {
        const genEdCategories = placeholderData.requirements.genEdCategories || [];

        courseData = {
          type: 'placeholder',
          code: `placeholder-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          title: placeholderData.title,
          credits: placeholderData.credits,
          placeholderType: placeholderData.placeholderType,
          notes: placeholderData.notes,
          isPlaceholder: true,
          requirements: {
            department: placeholderData.requirements.department || '',
            levelMin: placeholderData.requirements.levelMin
              ? parseInt(String(placeholderData.requirements.levelMin))
              : null,
            levelMax: placeholderData.requirements.levelMax
              ? parseInt(String(placeholderData.requirements.levelMax))
              : null,
            genEdCategories,
            keywords: placeholderData.requirements.keywords || [],
          },
          gen_ed: genEdCategories.length > 0,
          gen_ed_area: genEdCategories,
          gen_ed_requirements: genEdCategories,
        };

        if (context === 'other_credits') {
          courseData.sourceType = placeholderData.sourceType;
          courseData.source_type = placeholderData.sourceType;
          courseData.institution = placeholderData.institution;
          courseData.grade = placeholderData.grade || null;
        }
      }

      await onSubmit(courseData, targetSemester);
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
      setErrors({ submit: (error as { message?: string })?.message || 'Failed to add course' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const tabActiveClass: Record<string, string> = {
    red: 'border-primary text-primary',
    stone: 'border-stone-600 text-stone-600',
    indigo: 'border-indigo-600 text-indigo-600',
  };

  // The 'red' variant intentionally has no hover-darken class — the previous
  // `hover:bg-umd-red-dark` referenced a Tailwind shade that was never
  // defined, so it had no visual effect. Preserving the same (no-hover)
  // behavior post-codemod.
  const buttonClassMap: Record<string, string> = {
    red: 'bg-primary',
    stone: 'bg-stone-600 hover:bg-stone-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
  };

  const selectedBgClassMap: Record<string, string> = {
    red: 'bg-red-50',
    stone: 'bg-stone-50',
    indigo: 'bg-indigo-50',
  };

  const buttonClass = buttonClassMap[accentColor];
  const selectedBgClass = selectedBgClassMap[accentColor];

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      ariaLabel={getModalTitle()}
      maxWidth="max-w-[calc(100vw-1rem)] sm:max-w-xl"
      className="max-h-[90vh] overflow-hidden flex flex-col"
    >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">{getModalTitle()}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {!editingPlaceholder && (
          <div className="px-6 pt-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex space-x-4">
              <button
                onClick={() => setMode('search')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  mode === 'search'
                    ? tabActiveClass[accentColor]
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Search Courses
              </button>
              <button
                onClick={() => setMode('placeholder')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  mode === 'placeholder'
                    ? tabActiveClass[accentColor]
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Create Placeholder
              </button>
            </div>
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {mode === 'search' ? (
            <SearchPanel
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchLoading={searchLoading}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortedResults={sortedResults}
              selectedCourse={selectedCourse}
              setSelectedCourse={setSelectedCourse}
              isCourseInPlan={isCourseInPlan}
              isCourseBookmarked={isCourseBookmarked}
              context={context}
              placeholderData={placeholderData}
              handleInputChange={handleInputChange}
              selectedBgClass={selectedBgClass}
              accentColor={accentColor}
              setMode={setMode}
              errors={errors}
            />
          ) : (
            <PlaceholderForm
              editingPlaceholder={editingPlaceholder}
              placeholderData={placeholderData}
              handleInputChange={handleInputChange}
              handleTemplateSelect={handleTemplateSelect}
              handleGenEdToggle={handleGenEdToggle}
              handleAddKeyword={handleAddKeyword}
              handleRemoveKeyword={handleRemoveKeyword}
              keywordInput={keywordInput}
              setKeywordInput={setKeywordInput}
              showRequirements={showRequirements}
              setShowRequirements={setShowRequirements}
              context={context}
              targetSemester={targetSemester}
              accentColor={accentColor}
              buttonClass={buttonClass}
              errors={errors}
            />
          )}

          {errors.plc && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{errors.plc}</p>
            </div>
          )}

          {errors.submit && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || (mode === 'search' && !selectedCourse)}
            className={`px-4 py-2 text-white ${buttonClass} rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {editingPlaceholder
              ? 'Save Changes'
              : context === 'other_credits'
                ? 'Add Credit'
                : 'Add Course'}
          </button>
        </div>
    </ModalShell>
  );
};

export default UnifiedCourseModal;
